<script setup lang="ts">
import { ulid } from "ulid";
import { inject, computed, ref, onMounted } from "vue";

const props = withDefaults(
  defineProps<{
    id?: string;
    title?: string;
    opened?: boolean;
    class?: string;
    headerClass?: string;
    headerDirection?: "normal" | "reverse";
  }>(),
  {
    opened: false,
    headerDirection: "normal",
  }
);
const localId = props.id || ulid();
const group = inject<any>("accordionGroup", null);
const localOpen = ref(props.opened);

onMounted(() => {
  if (group && props.opened) {
    group.registerInitialOpened(localId);
  }
});

const accordionOpen = computed({
  get: () => (group ? group.openedId.value === localId : localOpen.value),
  set: (val) => {
    if (group) {
      group.requestOpen(localId);
    } else {
      localOpen.value = val;
    }
  },
});
</script>

<template>
  <div :class="props.class">
    <button
      :id="`accordion-title-${localId}`"
      class="cursor-pointer hover:bg-bar flex w-full flex-row items-center justify-items-start gap-3 text-left ring-0"
      :class="[`${!accordionOpen ? '' : ''}`, headerClass]"
      @click.prevent="accordionOpen = !accordionOpen"
      :aria-expanded="accordionOpen"
      :aria-controls="`accordion-text-${localId}`"
    >
      <svg v-if="headerDirection === 'normal'" class="fill-main shrink-0" width="12" height="12">
        <rect
          y="5"
          width="12"
          height="2"
          rx="1"
          class="origin-center transform transition duration-200 ease-out"
          :class="{ '!rotate-180': accordionOpen }"
        />
        <rect
          y="5"
          width="12"
          height="2"
          rx="1"
          class="origin-center rotate-90 transform transition duration-200 ease-out"
          :class="{ '!rotate-180': accordionOpen }"
        />
      </svg>
      <span class="" v-if="title">{{ title }}</span>
      <slot name="header" />
      <svg v-if="headerDirection === 'reverse'" class="fill-main shrink-0" width="12" height="12">
        <rect
          y="5"
          width="12"
          height="2"
          rx="1"
          class="origin-center transform transition duration-200 ease-out"
          :class="{ '!rotate-180': accordionOpen }"
        />
        <rect
          y="5"
          width="12"
          height="2"
          rx="1"
          class="origin-center rotate-90 transform transition duration-200 ease-out"
          :class="{ '!rotate-180': accordionOpen }"
        />
      </svg>
    </button>
    <div
      :id="`accordion-text-${localId}`"
      role="region"
      :aria-labelledby="`accordion-title-${localId}`"
      class="grid overflow-hidden text-sm transition-all duration-300 ease-in-out"
      :class="[accordionOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0']"
    >
      <div class="overflow-hidden">
        <p class="">
          <slot name="contents" />
        </p>
      </div>
    </div>
  </div>
</template>
